'use client';

import { FC, useState, useEffect, JSX } from 'react';
import { FaFire, FaRegLightbulb, FaRegSmile } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

interface BlogReactionsProps {
  slug: string;
  className?: string;
  initialReactions?: { fire: number; clap: number; idea: number };
}

type ReactionType = 'fire' | 'clap' | 'idea';

interface ReactionData {
  fire: number;
  clap: number;
  idea: number;
}

const BlogReactions: FC<BlogReactionsProps> = ({ slug, className }) => {
  const [reactions, setReactions] = useState<ReactionData>({ fire: 0, clap: 0, idea: 0 });
  const [loading, setLoading] = useState<ReactionType | null>(null);

  // Fetch reactions from the server on mount
  useEffect(() => {
    const fetchReactions = async () => {
      try {
        const res = await fetch(`/api/blog/${slug}/reactions`);
        if (!res.ok) throw new Error('Failed to fetch reactions');
        const data: ReactionData = await res.json();
        setReactions(data);
      } catch (err) {
        console.warn('Could not fetch reactions for', slug);
      }
    };
    fetchReactions();
  }, [slug]);

  const handleReaction = async (type: ReactionType) => {
    if (loading) return;
    setLoading(type);

    // Optimistic UI update
    setReactions(prev => ({ ...prev, [type]: prev[type] + 1 }));

    try {
      const res = await fetch(`/api/blog/${slug}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      if (!res.ok) throw new Error('Failed to update reaction');

      const data: ReactionData = await res.json();
      setReactions(data); // Sync with DB response
    } catch {
      toast.error('Failed to register reaction');
      // Rollback optimistic update
      setReactions(prev => ({ ...prev, [type]: prev[type] - 1 }));
    } finally {
      setLoading(null);
    }
  };

  const reactionButtons: [ReactionType, number, JSX.Element, string][] = [
    ['fire', reactions.fire, <FaFire />, 'text-red-500 hover:text-red-600'],
    ['clap', reactions.clap, <FaRegSmile />, 'text-yellow-500 hover:text-yellow-600'],
    ['idea', reactions.idea, <FaRegLightbulb />, 'text-indigo-500 hover:text-indigo-600'],
  ];

  return (
    <div className={`flex gap-6 mt-6 ${className ?? ''}`}>
      {reactionButtons.map(([type, count, icon, color]) => (
        <motion.button
          key={type}
          onClick={() => handleReaction(type)}
          disabled={loading === type}
          className={`flex items-center gap-1 transition disabled:opacity-50 cursor-pointer ${color}`}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          {icon} {count}
        </motion.button>
      ))}
    </div>
  );
};

export default BlogReactions;
